import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import {
  BodyText,
  Button,
  DisplayText,
  IconLink,
  IllusLocation,
  LoadingPlaceholder,
  Modal,
  ModalDialogue,
  theme,
} from '@sg/garnish';

import { useCartHandoff, useCartHandoffContent } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const CartHandoffModal = () => {
  const { formatMessage } = useIntl();

  // ───────────────────────────────────────────────────────────────────────────

  const {
    hasCartHandoffRequest,
    hasActiveBag,
    hasConflicts,
    isHandingOffCart,
    hasHandoffCompleted,
    isReorderUnavailable,
    reorderConflicts,
    startCartHandoff,
    cancelCartHandoff,
  } = useCartHandoff();

  const { title, body, cta } = useCartHandoffContent({
    isHandingOffCart,
    hasHandoffCompleted,
    isReorderUnavailable,
    hasConflicts,
    hasActiveBag,
    reorderConflicts,
  });

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      size="medium"
      imageSrc={IllusLocation}
      visible={hasCartHandoffRequest && !hasHandoffCompleted}
      accessibilityLabel={formatMessage(messages.a11yLabel)}
    >
      <View style={styles.content}>
        <Modal.Row style={styles.titleContainer}>
          <DisplayText sizeMatch={['24']}>{title}</DisplayText>

          {isHandingOffCart ? null : (
            <IconLink
              name="IconClose"
              palette="muted"
              width={32}
              height={32}
              iconSize={20}
              aria-label={formatMessage(messages.close)}
              onPress={cancelCartHandoff}
            />
          )}
        </Modal.Row>

        <Modal.Row>
          {body ? (
            <BodyText sizeMatch={['16']}>{body}</BodyText>
          ) : (
            <LoadingPlaceholder rowHeight={48} palette="cream" />
          )}
        </Modal.Row>

        <Modal.Footer withoutTopBorder>
          <Button
            palette="primary"
            size="large"
            accessibilityRole="button"
            aria-label={cta}
            isLoading={isHandingOffCart}
            onPress={isReorderUnavailable ? cancelCartHandoff : startCartHandoff}
          >
            {cta}
          </Button>
        </Modal.Footer>
      </View>
    </ModalDialogue>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yLabel: {
    defaultMessage: 'Cart Handoff Dialogue',
    description: 'Cart Handoff | A11y Label',
  },
  close: {
    defaultMessage: 'Close',
    description: 'Cart Handoff | Close',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    flex: 1,
    justifyContent: 'space-between',
    gap: theme.spacing['4'],
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['4'],
  },
});
